// Per-plan token bonus for the heart pill, matching the platform's formula:
//   bonus = (GB premium-tokens-amount || settings.monthly_tokens_bonus)
//         + instant[duration] (GB premium-instant-tokens-amount) + plan.tokensExtraBonus
import { useEffect, useState } from 'react';
import { useFeatureValue } from '@growthbook/growthbook-react';

const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL;
const API_BASE_URL = `${SWIPEY_BASE_URL}/api/v1`;
const API_KEY = process.env.REACT_APP_API_KEY;

// GB feature keys (identical to the platform's GB_FEATURES entries).
const GB_PREMIUM_TOKENS_AMOUNT = 'premium-tokens-amount';
const GB_PREMIUM_INSTANT_TOKENS_AMOUNT = 'premium-instant-tokens-amount';

// Parse a "X|Y|Z" instant-tokens string into the platform's duration map.
// Mirrors calculateTokensExtraBonus: index 0→1mo, 1→3mo, 2→12mo.
const parseInstantTokens = (instantTokens) => {
  const [oneMonth, threeMonth, twelveMonth] = (instantTokens || '0|0|0')
    .split('|')
    .map((v) => Number(v) || 0);
  return { 1: oneMonth, 3: threeMonth, 12: twelveMonth };
};

// instantTokensOverride: force "1mo|3mo|12mo" instead of reading the GB flag.
export function useTokenBonus({ instantTokensOverride } = {}) {
  // GB flags — same source + defaults as the platform.
  const gbMonthlyTokens = useFeatureValue(GB_PREMIUM_TOKENS_AMOUNT, '');
  const gbInstantTokens = useFeatureValue(
    GB_PREMIUM_INSTANT_TOKENS_AMOUNT,
    '0|0|0',
  );

  // Settings fallback for the monthly bonus (used only if the GB flag is empty),
  // mirroring the platform's `Number(gbMonthlyTokens || monthlyTokens)`.
  const [settingsMonthlyBonus, setSettingsMonthlyBonus] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/public-configs/system-settings`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': API_KEY,
            },
          },
        );
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && typeof data?.monthly_tokens_bonus === 'number') {
          setSettingsMonthlyBonus(data.monthly_tokens_bonus);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('[paywall-popup] system-settings fetch failed', err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // GB monthly wins; fall back to the settings value (platform precedence).
  const monthlyTokensBonus = Number(gbMonthlyTokens) || settingsMonthlyBonus;
  const instantByDuration = parseInstantTokens(
    instantTokensOverride ?? gbInstantTokens,
  );

  const getBonusForPlan = (plan) => {
    const instant = instantByDuration[plan?.duration_months] || 0;
    return monthlyTokensBonus + instant + (plan?.tokensExtraBonus ?? 0);
  };

  return { getBonusForPlan, monthlyTokensBonus, isLoading };
}
